/**
 * WHERE a document form's field is stored — resolved once, for every surface.
 *
 * The engine answers this in two places that have to agree: `build_payload`
 * (`er/execution.py:2126`) decides which column of which transaction a value is
 * posted to, and `_routes_to_sink` (`:1492`) decides whether it reaches a
 * transaction at all or lands in the generic capture object. Between them sits
 * an INFERENCE — "does the primary have a column of this key?" — whose own
 * docstring names the hazard it carries: adding a column to the transaction
 * moves a field out of the sink and strands its captured history, and a typo in
 * a field key routes it to the sink rather than failing. Both are invisible.
 *
 * They were invisible in the designer too, which is what this module is for. It
 * mirrors those rules the way `fieldGroups.ts` mirrors `er/paths.py`: one
 * implementation of the vocabulary, so a panel can never tell an author their
 * agreement date goes somewhere the save does not put it.
 *
 * **It resolves, it does not decide.** Nothing here writes to a model. A field
 * whose destination was never declared still comes back with the answer the
 * engine would reach today, marked `declared: false`, so the author can see
 * what they are relying on before they pin it down.
 *
 * Pure — no React, no network. `test_er_destination.mjs` runs it on fixtures.
 */

import type { SourceColumn, SourceForm, SourceTxn } from './sources'
import type { FieldRow, FormModel, TxnRow } from './types'

/**
 * `"INVOICE.2.QTY"` → `["INVOICE", "2", "QTY"]`, missing segments empty.
 *
 * Lives here rather than in `fieldGroups` because the path grammar and the
 * destination rules are one subject, and `fieldGroups` needs the destination to
 * group by it. `fieldGroups` re-exports it, so every existing importer is
 * unchanged.
 */
export function parsePath(raw: unknown): [string, string, string] {
  const parts = String(raw || '').trim().split('.')
  return [
    (parts[0] || '').trim().toUpperCase(),
    (parts[1] || '').trim(),
    (parts[2] || '').trim().toUpperCase(),
  ]
}

/** The three places a value can end up. */
export type DestinationKind = 'transaction' | 'document-data' | 'nowhere'

/** Why a field stores nothing — each one a different conversation to have. */
export type NowhereReason =
  /** A merge is read-only evidence; it resolves and is shown. */
  | 'shown'
  /** A signature is filed with the document, not written to a column. */
  | 'signed'
  /** A repeat is the loop over rows, not a value of its own. */
  | 'loop'
  /** A calculation with no `map` is shown and writes nowhere — by design. */
  | 'calc-unmapped'
  /**
   * A calculation with no `map` whose key COLLIDES with a column of the
   * primary. `build_payload` excludes a calc from the same-key convention and
   * `_routes_to_sink` sees the column and keeps it out of the sink, so the
   * value is written nowhere at all — the one case where two correct rules
   * meet and a value falls between them.
   */
  | 'calc-shadowed'
  /** `capture: "typed"` declared, and no column of that key exists. */
  | 'typed-no-column'
  /** The map names a transaction this document does not cover. */
  | 'unknown-transaction'
  /** The map names a form the covered transaction does not have. */
  | 'unknown-form'
  | ''

export interface Destination {
  kind: DestinationKind
  /**
   * True when the MODEL says so — a `map`, or an explicit `capture`. False
   * when this is the engine's inference, which is the thing worth showing.
   */
  declared: boolean
  reason: NowhereReason
  /** For `transaction`: where, in the path vocabulary. */
  txnIdent: string
  formNo: string
  column: string
  /** The resolved column, when the transaction's model has been loaded. */
  col: SourceColumn | null
  /** What a person calls the transaction and the form it lands on. */
  txnTitle: string
  formTitle: string
  /** The database table, when the model is loaded — evidence, not a target. */
  table: string
}

function nowhere(reason: NowhereReason, declared = true): Destination {
  return {
    kind: 'nowhere', declared, reason,
    txnIdent: '', formNo: '', column: '', col: null,
    txnTitle: '', formTitle: '', table: '',
  }
}

function documentData(declared: boolean): Destination {
  return {
    kind: 'document-data', declared, reason: '',
    txnIdent: '', formNo: '', column: '', col: null,
    txnTitle: '', formTitle: '', table: '',
  }
}

/**
 * Does this form hold a column of that key?
 *
 * `_backs` (`er/execution.py:1339`) asks the resolver built by
 * `_form_key_resolver`, which indexes each column by BOTH its form key and its
 * `db_name`. Asking only one of them would report a column missing whenever the
 * two spellings differ, which is the ordinary case (`AGREEMENT_DATE` against
 * `agreement_date`).
 */
export function columnOfKey(
  form: SourceForm | null, key: string,
): SourceColumn | null {
  if (!form || !key) return null
  const wanted = String(key).trim().toLowerCase()
  return form.columns.find((c) => c.name.toLowerCase() === wanted
    || c.dbName.toLowerCase() === wanted) || null
}

function formOf(txn: SourceTxn | null, formNo: string): SourceForm | null {
  if (!txn) return null
  return txn.forms.find((f) => f.formNo === String(formNo)) || null
}

function txnOf(catalog: SourceTxn[], ident: string): SourceTxn | null {
  if (!ident) return null
  const wanted = ident.toUpperCase()
  return (catalog || []).find((t) => t.ident === wanted) || null
}

function landOn(txn: SourceTxn | null, form: SourceForm | null,
  ident: string, formNo: string, column: string,
  declared: boolean): Destination {
  return {
    kind: 'transaction', declared, reason: '',
    txnIdent: ident, formNo, column: column.toUpperCase(),
    col: columnOfKey(form, column),
    txnTitle: txn?.title || ident.replace(/_/g, ' '),
    formTitle: form?.title || (formNo === '1' ? '' : `Form ${formNo}`),
    table: form?.table || '',
  }
}

/** The block a field rides inside, and the form that block loops over. */
function blockPath(fields: FieldRow[], blockKey: string): [string, string] {
  const block = (fields || []).find(
    (f) => f.kind === 'repeat' && f.key === blockKey)
  if (!block) return ['', '']
  const [txn, formNo] = parsePath(block.path)
  return [txn, formNo]
}

/**
 * Where this field is stored — the same answer the engine reaches.
 *
 * The order is the engine's, and it matters: a `map` wins over everything, a
 * declared `capture` wins over the inference, and the inference is only ever
 * the last word. Anything the catalog cannot confirm comes back as the kind it
 * would be with the evidence missing, rather than as a guess dressed up as a
 * fact — `col` is null and the caller shows the path it was given.
 */
export function destinationOf(
  field: FieldRow, model: FormModel, catalog: SourceTxn[],
): Destination {
  return destinationIn(
    field,
    model?.form?.templates?.[0]?.fields || [],
    model?.form?.transactions || [],
    catalog)
}

/**
 * The same answer, from the pieces rather than the whole model.
 *
 * `buildFieldGroups` holds exactly these three and no `FormModel`, and it has
 * to group by destination — a field kept as document data filed under the
 * transaction it never reaches is the screen the whole report was about.
 */
export function destinationIn(
  field: FieldRow, fields: FieldRow[], txns: TxnRow[], catalog: SourceTxn[],
): Destination {
  const kind = String(field?.kind || 'entry')
  if (kind === 'merge') return nowhere('shown')
  if (kind === 'signature') return nowhere('signed')
  if (kind === 'repeat') return nowhere('loop')
  const primaryRow = txns.find((t) => t.role === 'primary') || txns[0]
  const primaryIdent = String(
    primaryRow?.alias || primaryRow?.obj_name || '').trim().toUpperCase()
  const primaryFormNo = String(primaryRow?.form_no || '1') || '1'

  // 1 — an explicit map. The path IS the declaration, whatever else is said.
  const mapped = String(field.map || '').trim()
  if (mapped) {
    const [ident, formNo, column] = parsePath(mapped)
    const txn = txnOf(catalog, ident)
    // A transaction the document does not cover is not a destination. The
    // engine's `_mine` simply never matches it, so the value is posted by
    // nobody — which reads on screen as a field that saves and does not.
    if (!txn && !txns.some(
      (t) => String(t.alias || t.obj_name || '').toUpperCase() === ident)) {
      return nowhere('unknown-transaction')
    }
    const form = formOf(txn, formNo || '1')
    if (txn && txn.forms.length && !form) return nowhere('unknown-form')
    return landOn(txn, form, ident, formNo || '1', column, true)
  }

  // Inside a repeating block, "the same key" is asked of the BLOCK's own form,
  // never of the header — `_table_backing` (`er/execution.py:1460`). Asking the
  // header would claim every line column was backed and write each one twice.
  const block = String(field.in_table || '').trim()
  if (block) {
    const [blockIdent, blockForm] = blockPath(fields, block)
    const blockTxn = txnOf(catalog, blockIdent)
    const form = formOf(blockTxn, blockForm)
    const declaredCapture = String(field.capture || '').trim().toLowerCase()
    if (declaredCapture === 'sink') return documentData(true)
    const found = columnOfKey(form, field.key)
    if (declaredCapture === 'typed') {
      return found
        ? landOn(blockTxn, form, blockIdent, blockForm, found.dbName, true)
        : nowhere('typed-no-column')
    }
    // A block bound to no transaction has no form to ask, and every unmapped
    // field in it is the sink's — the same call `_capture_table_fields` makes.
    if (!blockIdent) return documentData(false)
    if (!blockTxn || !blockTxn.forms.length) return documentData(false)
    return found
      ? landOn(blockTxn, form, blockIdent, blockForm, found.dbName, false)
      : documentData(false)
  }

  // 2 — a declared capture at document level.
  const primary = txnOf(catalog, primaryIdent)
  const headerForm = formOf(primary, primaryFormNo)
  // A READ-ONLY primary has no column for a typed value, so the same-key
  // convention cannot fire: `_writable_transactions` drops the source before
  // the save reaches it. That is always true on a form built on a visual —
  // every column of a result set is computed — and finding a same-named
  // column there would report a destination the save will never write to.
  const found = primary?.readOnly ? null : columnOfKey(headerForm, field.key)
  const declaredCapture = String(field.capture || '').trim().toLowerCase()
  if (declaredCapture === 'sink') return documentData(true)
  if (declaredCapture === 'typed') {
    if (!found) return nowhere('typed-no-column')
    // Still only an ENTRY field rides the same-key convention into a column.
    // A calc that says `typed` and names no column is therefore shadowed by
    // the very column it was declared against: `_routes_to_sink` keeps it out
    // of document data BECAUSE that column exists, and `build_payload` will
    // not give it the column. Declared, and still written nowhere.
    if (kind !== 'entry') return nowhere('calc-shadowed')
    return landOn(primary, headerForm, primaryIdent, primaryFormNo,
      found.dbName, true)
  }

  // 3 — the inference, which is where both silent failures live.
  if (kind === 'calc') {
    // Two correct rules, and it depends on which one bites. With a colliding
    // column, `build_payload` excludes the calc from the same-key convention
    // and `_routes_to_sink` keeps it out of document data because the column
    // exists — so the value falls between them and is written nowhere. With
    // NO colliding column, `_capture_fields` accepts a calc like any entry
    // field and it is kept as document data, which is what makes a computed
    // total on a document survive at all.
    return found ? nowhere('calc-shadowed', false) : documentData(false)
  }
  if (!found) return documentData(false)
  return landOn(primary, headerForm, primaryIdent, primaryFormNo,
    found.dbName, false)
}

/**
 * Whether this form can actually store anything as document data.
 *
 * Three prerequisites, none of them per-field, and a form missing any of them
 * accepts the declaration and writes nothing:
 *
 * * `capture.mode` must be `generic` — `_post_generic_capture` returns early
 *   otherwise (`er/execution.py:1009`);
 * * `capture.obj_name` must name a real transaction object to hold the rows;
 * * there must be an execution to hang the readings off, which
 *   `governance: "none"` never creates (`:1656`, `:1722`).
 */
export interface DocumentDataReadiness {
  ready: boolean
  /** Each unmet prerequisite, in the order the author would fix them. */
  missing: ('governance' | 'mode' | 'object')[]
}

export function documentDataReadiness(model: FormModel): DocumentDataReadiness {
  const form = model?.form
  const capture = (form?.capture || {}) as Record<string, unknown>
  const missing: DocumentDataReadiness['missing'] = []
  if (String(form?.governance || 'none') === 'none') missing.push('governance')
  if (String(capture.mode || 'typed') !== 'generic') missing.push('mode')
  if (!String(capture.obj_name || '').trim()) missing.push('object')
  return { ready: missing.length === 0, missing }
}

/** Whether the type of this field is the transaction's to decide, not ours. */
export function typeIsOwned(dest: Destination): boolean {
  return dest.kind === 'transaction' && dest.col !== null
}

/** The one-line sentence a panel reads, split so the nouns can be emphasised. */
export function describeDestination(dest: Destination): [string, string, string] {
  switch (dest.kind) {
    case 'transaction': {
      const column = dest.col?.heading || dest.column
      const form = dest.formNo === '1' ? '' : ` · ${dest.formTitle}`
      return ['Saved into', `${dest.txnTitle}${form}`, `— ${column}`]
    }
    case 'document-data':
      return ['Kept as', 'document data', '— not a column of any transaction']
    default:
      return [NOWHERE_WORDS[dest.reason] || 'Stored nowhere', '', '']
  }
}

export const NOWHERE_WORDS: Record<string, string> = {
  shown: 'Shown, not stored — a merge resolves and is read-only',
  signed: 'Filed with the document, not written to a column',
  loop: 'The loop over rows — its columns are stored, this is not',
  'calc-unmapped': 'Worked out and shown. It writes nowhere until you give it somewhere',
  'calc-shadowed': 'Worked out and written NOWHERE — the primary has a column of this name, which keeps it out of document data, and a calculation never takes a column by name alone. Give it somewhere explicitly',
  'typed-no-column': 'Declared as a transaction column, and there is no column of this name',
  'unknown-transaction': 'The target names a transaction this document does not cover',
  'unknown-form': 'The target names a form this transaction does not have',
}
